import { defineStore } from 'pinia'
import { ref } from 'vue'
import { notificationsApi } from '@/api/resources'
import { useAuthStore } from './auth'
import type { AppNotification } from '@/types/api'

export const useNotificationStore = defineStore('notifications', () => {
  const recent = ref<AppNotification[]>([])
  const unreadCount = ref(0)
  const loading = ref(false)

  function syncAuthBadge(): void {
    useAuthStore().setUnreadCount(unreadCount.value)
  }

  async function loadRecent(limit = 6): Promise<void> {
    loading.value = true
    try {
      const collection = await notificationsApi.list({ itemsPerPage: limit, page: 1 })
      recent.value = collection.items
    } finally {
      loading.value = false
    }
  }

  async function refreshUnreadCount(): Promise<void> {
    const { count } = await notificationsApi.unreadCount()
    unreadCount.value = count
    syncAuthBadge()
  }

  async function markRead(id: number, read: boolean): Promise<void> {
    const updated = await notificationsApi.markRead(id, read)
    recent.value = recent.value.map((item) => (item.id === id ? updated : item))
    unreadCount.value = Math.max(0, unreadCount.value + (read ? -1 : 1))
    syncAuthBadge()
  }

  async function markAllRead(): Promise<number> {
    const { marked } = await notificationsApi.readAll()
    recent.value = recent.value.map((item) =>
      item.read ? item : { ...item, read: true, readAt: new Date().toISOString() },
    )
    unreadCount.value = 0
    syncAuthBadge()
    return marked
  }

  function reset(): void {
    recent.value = []
    unreadCount.value = 0
  }

  return { recent, unreadCount, loading, loadRecent, refreshUnreadCount, markRead, markAllRead, reset }
})
